import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Box, Typography, TextField, MenuItem, Button, IconButton, Divider,
  Switch, FormControlLabel, Grid, Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { v4 as uuid } from 'uuid';
import DropdownTextField from '../form-components/DropdownTextField';
import LocalText from '../common/LocalText';
import { textFieldSx, selectFieldSx, dropdownMenuProps } from '../../utils/dropdownStyles';
import { hasRequiredName, isDateRangeValid } from '../../utils/validators';
import { getContentEventTypes, getPlatformTypes, getEventFormats } from '../../api/eventApi';
import { getPersonalitiesByEntity } from '../../api/personalityApi';
import { useSpace } from '../../context/SpaceContext';
import { useSpaceId } from '../../hooks/useSpaceId';
import PersonalityFormModal from './PersonalityFormModal';

const CREATE_NEW_PERSONALITY = '__create__';

const SectionLabel = ({ children, required }) => (
  <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#5383b3', fontSize: 10, mb: 0.5, mt: 1.5 }}>
    <LocalText>{children}</LocalText>
    {required && <span style={{ color: '#d32f2f', marginLeft: 3 }}>*</span>}
  </Typography>
);

const toLocalInput = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Minutes between two datetime-local input strings, or null if either is missing/invalid.
const minutesBetween = (start, end) => {
  if (!start || !end) return null;
  const s = new Date(start);
  const e = new Date(end);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime())) return null;
  const diff = Math.round((e.getTime() - s.getTime()) / 60000);
  return diff > 0 ? diff : null;
};

const CreateEventModal = ({ open, onClose, onSave, initial, approvalMode = false, readOnly = false }) => {
  const { space } = useSpace();
  const spaceId = useSpaceId();
  const [nameValues, setNameValues] = useState([{ localeId: 'en_us', value: '' }]);
  const [descValues, setDescValues] = useState([{ localeId: 'en_us', value: '' }]);
  const [eventTypeId, setEventTypeId]       = useState('');
  const [platformTypeId, setPlatformTypeId] = useState('');
  const [eventFormatId, setEventFormatId]   = useState('');
  const [eventUrl, setEventUrl]             = useState('');
  const [scheduledStart, setScheduledStart] = useState('');
  const [scheduledEnd, setScheduledEnd]     = useState('');
  const [isLive, setIsLive]         = useState(true);
  const [capacity, setCapacity]     = useState('');
  const [hostingStreamUrl, setHostingStreamUrl] = useState('');
  const [subtitleEnabled, setSubtitleEnabled]   = useState(false);

  // Personality is a reusable, entity-owned record — picked from a dropdown scoped to this
  // space's entity (space.entityId, never user-chosen), not authored inline per event anymore.
  const [personalityId, setPersonalityId]   = useState('');
  const [personalities, setPersonalities]   = useState([]);
  const [personalityModalOpen, setPersonalityModalOpen] = useState(false);
  const [personalityModalMode, setPersonalityModalMode] = useState('create'); // 'create' | 'edit'

  const [eventTypes, setEventTypes]       = useState([]);
  const [platformTypes, setPlatformTypes] = useState([]);
  const [eventFormats, setEventFormats]   = useState([]);

  useEffect(() => {
    if (!open) return;
    getContentEventTypes().then(setEventTypes).catch(() => setEventTypes([]));
    getPlatformTypes().then(setPlatformTypes).catch(() => setPlatformTypes([]));
    getEventFormats().then(setEventFormats).catch(() => setEventFormats([]));
  }, [open]);

  // Refetches whenever the entity changes too (e.g. the user switched spaces), not just on open.
  useEffect(() => {
    if (!open || !space?.entityId) return;
    getPersonalitiesByEntity(space.entityId).then(setPersonalities).catch(() => setPersonalities([]));
  }, [open, space?.entityId]);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setNameValues(
        initial.localizedName?.values?.length > 0
          ? initial.localizedName.values.map((v) => ({ localeId: v.localeId, value: v.value }))
          : [{ localeId: 'en_us', value: '' }]
      );
      setDescValues(
        initial.localizedDescription?.values?.length > 0
          ? initial.localizedDescription.values.map((v) => ({ localeId: v.localeId, value: v.value }))
          : [{ localeId: 'en_us', value: '' }]
      );
      setEventTypeId(initial.eventTypeId ?? '');
      setPlatformTypeId(initial.platformTypeId ?? '');
      setEventFormatId(initial.eventFormatId ?? '');
      setEventUrl(initial.eventUrl ?? '');
      setScheduledStart(toLocalInput(initial.scheduledStart));
      setScheduledEnd(toLocalInput(initial.scheduledEnd));
      setIsLive(initial.isLive ?? true);
      setCapacity(initial.capacity ?? '');
      setHostingStreamUrl(initial.hostingStreamUrl ?? '');
      setSubtitleEnabled(initial.subtitleEnabled ?? false);
      setPersonalityId(initial.personalityId ?? '');
    } else {
      setNameValues([{ localeId: 'en_us', value: '' }]);
      setDescValues([{ localeId: 'en_us', value: '' }]);
      setEventTypeId('');
      setPlatformTypeId('');
      setEventFormatId('');
      setEventUrl('');
      setScheduledStart('');
      setScheduledEnd('');
      setIsLive(true);
      setCapacity('');
      setHostingStreamUrl('');
      setSubtitleEnabled(false);
      setPersonalityId('');
    }
  }, [open, initial?.id]);

  const isNameValid     = hasRequiredName(nameValues);
  const isScheduleValid = Boolean(scheduledStart) && Boolean(scheduledEnd) && isDateRangeValid(scheduledStart, scheduledEnd);
  const isCapacityValid = capacity === '' || Number(capacity) >= 0;
  const isValid = isNameValid && Boolean(eventTypeId) && Boolean(platformTypeId) && isScheduleValid && isCapacityValid;
  const isEdit  = Boolean(initial);

  const selectedFormat = eventFormats.find((f) => String(f.id) === String(eventFormatId));
  const formatName = (selectedFormat?.Name || '').trim().toUpperCase();
  // Prefix match, not exact — the format is named "2D Hosting" (was plain "2D"); this
  // keeps working either way, including against a DB that hasn't re-run the rename yet.
  const is2D = formatName.startsWith('2D');
  const is3D = formatName === '3D';

  const durationMinutes = minutesBetween(scheduledStart, scheduledEnd);

  const titleText = readOnly
    ? 'View Event'
    : isEdit ? 'Edit Event' : 'New Event';

  const saveLabel = approvalMode
    ? 'Submit for Approval'
    : isEdit ? 'Save Changes' : 'Create Event';

  const handlePersonalitySelectChange = (e) => {
    const val = e.target.value;
    if (val === CREATE_NEW_PERSONALITY) {
      setPersonalityModalMode('create');
      setPersonalityModalOpen(true);
      return;
    }
    setPersonalityId(val);
  };

  const openEditPersonality = () => {
    if (!personalityId) return;
    setPersonalityModalMode('edit');
    setPersonalityModalOpen(true);
  };

  const handlePersonalitySaved = (saved) => {
    setPersonalities((prev) => {
      const others = prev.filter((p) => p.id !== saved.id);
      return [...others, { id: saved.id, name: saved.name, photoLink: saved.media?.linkLocalizations?.[0]?.mediaLink ?? null }];
    });
    setPersonalityId(saved.id);
  };

  const handleSubmit = async () => {
    if (!isValid || readOnly) return;
    try {
      await onSave({
        localizedName: {
          key: initial?.localizedName?.key ?? uuid(),
          values: nameValues,
        },
        localizedDescription: descValues.some((v) => v.value) ? {
          key: initial?.localizedDescription?.key ?? uuid(),
          values: descValues,
        } : null,
        eventTypeId:     Number(eventTypeId),
        platformTypeId:  Number(platformTypeId),
        eventFormatId:   eventFormatId === '' ? null : Number(eventFormatId),
        eventUrl:        eventUrl.trim() === '' ? null : eventUrl.trim(),
        scheduledStart,
        scheduledEnd,
        scheduledLength: durationMinutes,
        isLive,
        capacity: capacity === '' ? null : Number(capacity),
        // Delivery only applies to 2D — omitted entirely for 3D/unselected so we never touch
        // an existing hosting link just because the format dropdown was toggled.
        ...(is2D ? {
          hostingStreamUrl: hostingStreamUrl.trim(),
          subtitleEnabled,
        } : {}),
        personalityId: personalityId === '' ? null : Number(personalityId),
      });
      onClose();
    } catch {
      // global api-error handler shows the toast; modal stays open for retry
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px', maxHeight: '85vh' } }}>
      <Box sx={{ bgcolor: '#0a326d', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, px: 3 }}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 700, fontSize: 16 }}>
          <LocalText>{titleText}</LocalText>
        </Typography>
        <IconButton size="small" onClick={(e) => { e.currentTarget.blur(); onClose(); }} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: 'white' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent dividers sx={{ p: 3, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>

          <SectionLabel required={!readOnly}>Event Name</SectionLabel>
          <DropdownTextField
            label="Event Name"
            values={nameValues}
            attribute="value"
            onChange={setNameValues}
            disabled={readOnly}
            required={!readOnly}
          />

          <SectionLabel>Description</SectionLabel>
          <DropdownTextField
            label="Description"
            values={descValues}
            attribute="value"
            onChange={setDescValues}
            disabled={readOnly}
          />

          <Divider sx={{ my: 1.5 }} />

          <Typography variant="caption" sx={{ display: 'block', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#0a326d', fontSize: 11 }}>
            <LocalText>Personality</LocalText>
          </Typography>
          <Typography variant="caption" sx={{ color: '#85a5c6', display: 'block', mb: 0.5 }}>
            <LocalText>Optional — who this event is about. Reusable across this entity's events.</LocalText>
          </Typography>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField
              select fullWidth size="small"
              value={personalities.length ? personalityId : ''}
              onChange={handlePersonalitySelectChange}
              disabled={readOnly}
              sx={textFieldSx}
              SelectProps={{ displayEmpty: true, sx: selectFieldSx, MenuProps: dropdownMenuProps }}
            >
              <MenuItem value=""><em><LocalText>None</LocalText></em></MenuItem>
              {personalities.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
              {!readOnly && (
                <MenuItem value={CREATE_NEW_PERSONALITY} sx={{ color: '#0a326d', fontWeight: 700 }}>
                  <LocalText>+ Create new personality</LocalText>
                </MenuItem>
              )}
            </TextField>
            {!readOnly && personalityId !== '' && (
              <IconButton
                size="small"
                onClick={openEditPersonality}
                title="Edit personality"
                sx={{ color: '#0a326d', bgcolor: '#eef4fb', border: '1px solid #c8d8ea', borderRadius: '8px', width: 36, height: 36, flexShrink: 0, '&:hover': { bgcolor: '#dde9f7' } }}
              >
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>

          <PersonalityFormModal
            open={personalityModalOpen}
            onClose={() => setPersonalityModalOpen(false)}
            onSaved={handlePersonalitySaved}
            personalityId={personalityModalMode === 'edit' ? Number(personalityId) : null}
            entityId={space?.entityId}
            spaceId={spaceId}
          />

          <Divider sx={{ my: 1.5 }} />

          <SectionLabel>Event URL</SectionLabel>
          <TextField
            fullWidth size="small" placeholder="https://your-event-page.com"
            value={eventUrl}
            onChange={(e) => setEventUrl(e.target.value)}
            disabled={readOnly} sx={textFieldSx}
          />

          <Divider sx={{ my: 1.5 }} />

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SectionLabel required={!readOnly}>Event Type</SectionLabel>
              <TextField
                select fullWidth size="small" value={eventTypes.length ? eventTypeId : ''}
                onChange={(e) => setEventTypeId(e.target.value)}
                disabled={readOnly} sx={textFieldSx}
              >
                {eventTypes.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.Type}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SectionLabel required={!readOnly}>Platform</SectionLabel>
              <TextField
                select fullWidth size="small" value={platformTypes.length ? platformTypeId : ''}
                onChange={(e) => setPlatformTypeId(e.target.value)}
                disabled={readOnly} sx={textFieldSx}
              >
                {platformTypes.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.PlatformName}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SectionLabel>Format</SectionLabel>
              <TextField
                select fullWidth size="small" value={eventFormats.length ? eventFormatId : ''}
                onChange={(e) => setEventFormatId(e.target.value)}
                disabled={readOnly} sx={textFieldSx}
              >
                <MenuItem value="">—</MenuItem>
                {eventFormats.map((f) => (
                  <MenuItem key={f.id} value={f.id}>{f.Name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SectionLabel>Capacity</SectionLabel>
              <TextField
                fullWidth size="small" type="number" placeholder="Unlimited"
                value={capacity}
                onChange={(e) => setCapacity(e.target.value)}
                disabled={readOnly} sx={textFieldSx}
                inputProps={{ min: 0 }}
                error={!isCapacityValid}
                helperText={!isCapacityValid ? 'Cannot be negative' : ''}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          <Grid container spacing={1.5}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SectionLabel required={!readOnly}>Start Time</SectionLabel>
              <TextField
                fullWidth size="small" type="datetime-local"
                value={scheduledStart}
                onChange={(e) => setScheduledStart(e.target.value)}
                disabled={readOnly} sx={textFieldSx}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SectionLabel required={!readOnly}>End Time</SectionLabel>
              <TextField
                fullWidth size="small" type="datetime-local"
                value={scheduledEnd}
                onChange={(e) => setScheduledEnd(e.target.value)}
                disabled={readOnly} sx={textFieldSx}
                error={Boolean(scheduledStart) && Boolean(scheduledEnd) && !isDateRangeValid(scheduledStart, scheduledEnd)}
                helperText={Boolean(scheduledStart) && Boolean(scheduledEnd) && !isDateRangeValid(scheduledStart, scheduledEnd) ? 'Must be after start time' : ''}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SectionLabel>Duration</SectionLabel>
              <Typography variant="body2" sx={{ color: durationMinutes ? '#1e3a5f' : '#85a5c6', fontWeight: 600, mt: 0.75 }}>
                {durationMinutes ? `${durationMinutes} minutes` : <LocalText>Set start &amp; end time</LocalText>}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }} sx={{ display: 'flex', alignItems: 'flex-end' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isLive}
                    onChange={(e) => setIsLive(e.target.checked)}
                    disabled={readOnly}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#0a326d' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#5383b3' },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3a5f' }}>
                    <LocalText>{isLive ? 'Live' : 'Recorded'}</LocalText>
                  </Typography>
                }
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 1.5 }} />

          {is2D && (
            <>
              <SectionLabel>Hosting — Stream URL</SectionLabel>
              <TextField
                fullWidth size="small" placeholder="https://stream-provider.com/..."
                value={hostingStreamUrl}
                onChange={(e) => setHostingStreamUrl(e.target.value)}
                disabled={readOnly} sx={textFieldSx}
              />
              <FormControlLabel
                sx={{ mt: 1, ml: 0 }}
                control={
                  <Switch
                    checked={subtitleEnabled}
                    onChange={(e) => setSubtitleEnabled(e.target.checked)}
                    disabled={readOnly}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#0a326d' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#5383b3' },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e3a5f' }}>
                    <LocalText>Subtitles Enabled</LocalText>
                  </Typography>
                }
              />
              <Typography variant="caption" sx={{ color: '#85a5c6', mt: 0.5 }}>
                <LocalText>
                  {isEdit
                    ? 'Set or update the stream this event plays from. Leave blank and save to remove hosting.'
                    : 'Optional — set the stream this event will play from. Can be added later.'}
                </LocalText>
              </Typography>
            </>
          )}

          {is3D && (
            <Alert severity="info" sx={{ borderRadius: '10px' }}>
              <LocalText>3D Hosting isn't available yet — this event will save without playback configuration.</LocalText>
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'grey.50', gap: 1.5 }}>
        {readOnly ? (
          <Button onClick={onClose} sx={{ color: '#85a5c6', fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
            <LocalText>Close</LocalText>
          </Button>
        ) : (
          <>
            <Button onClick={onClose} sx={{ color: '#85a5c6', fontWeight: 600, textTransform: 'none', borderRadius: '10px' }}>
              <LocalText>Cancel</LocalText>
            </Button>
            <Button
              variant="contained"
              disabled={!isValid}
              onClick={handleSubmit}
              sx={{
                bgcolor: '#0a326d', color: 'white', fontWeight: 700, textTransform: 'none',
                borderRadius: '10px', px: 3,
                boxShadow: '0 4px 12px rgba(10,50,109,0.25)',
                '&:hover': { bgcolor: '#0d3d85' },
                '&.Mui-disabled': { bgcolor: '#c8d8ea', color: 'white' },
              }}
            >
              <LocalText>{saveLabel}</LocalText>
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateEventModal;
